// src/pages/Banners.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Stack,
  Chip,
  Paper,
  AppBar,
  Toolbar,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  InputAdornment,
  Pagination,
} from "@mui/material";
import {
  Delete,
  Edit,
  UploadFile,
  Close,
  Image,
  Title,
  CheckCircle,
} from "@mui/icons-material";
import { URL } from "../constants/url";
import colors from "../constants/colors";

const Banners = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "lg"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);

  // Upload form
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadTitles, setUploadTitles] = useState([]);

  // Edit dialog
  const [editing, setEditing] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editFile, setEditFile] = useState(null);
  const [saving, setSaving] = useState(false);

  // Delete confirmation dialog
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    banner: null,
  });

  // Alert state (keeping original alert logic but with better styling)
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20; // 5 cards per row × 4 rows = 20 total

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${URL}/api/banners/get-list`);
      const data = await res.json();
      if (data.success) setBanners(data.banners || []);
    } catch (err) {
      console.error("fetchBanners", err);
      showAlert("Error fetching banners", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const showAlert = (message, severity = "success") => {
    setAlert({ open: true, message, severity });
  };

  const handleCloseAlert = () => {
    setAlert({ open: false, message: "", severity: "success" });
  };

  const handleFilesChange = (e) => {
    setUploadFiles(Array.from(e.target.files));
    setUploadTitles(new Array(e.target.files.length).fill(""));
  };

  const handleTitleChange = (idx, value) => {
    const arr = [...uploadTitles];
    arr[idx] = value;
    setUploadTitles(arr);
  };

  const handleUpload = async () => {
    if (uploadFiles.length === 0) {
      showAlert("Please select files to upload", "error");
      return;
    }
    
    const form = new FormData();
    uploadFiles.forEach((f) => form.append("bannerImages", f));
    uploadTitles.forEach((t) => form.append("titles", t));
    
    try {
      setSaving(true);
      const res = await fetch(`${URL}/api/banners/add-new`, {
        method: "POST",
        body: form,
      });
      const data = await res.json();
      if (data.success) {
        setUploadFiles([]);
        setUploadTitles([]);
        fetchBanners();
        setCurrentPage(1);
        document.getElementById("banner-files").value = "";
        showAlert("Banners uploaded successfully", "success");
      } else {
        showAlert(data.message || "Upload failed", "error");
      }
    } catch (err) {
      console.error("upload", err);
      showAlert("Upload failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    // Keep the original logic but update the dialog
    setDeleteConfirm({
      open: true,
      banner: banners.find(b => b._id === id),
    });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.banner) return;
    
    try {
      const res = await fetch(`${URL}/api/banners/delete/${deleteConfirm.banner._id}`, {
        method: "DELETE",
      });
      const data = await res.json();
      if (data.success) {
        fetchBanners();
        showAlert("Banner deleted successfully", "success");
      } else {
        showAlert(data.message || "Delete failed", "error");
      }
    } catch (err) {
      console.error("delete", err);
      showAlert("Delete failed", "error");
    } finally {
      closeDeleteConfirm();
    }
  };

  const closeDeleteConfirm = () => {
    setDeleteConfirm({
      open: false,
      banner: null,
    });
  };

  const openEdit = (banner) => {
    setEditing(banner);
    setEditTitle(banner.title || "");
    setEditFile(null);
  };

  const handleUpdate = async () => {
    if (!editing) return;
    try {
      setSaving(true);
      const form = new FormData();
      form.append("title", editTitle || "");
      if (editFile) form.append("bannerImages", editFile);

      const res = await fetch(`${URL}/api/banners/update/${editing._id}`, {
        method: "PUT",
        body: form,
      });
      const data = await res.json();
      if (data.success) {
        setEditing(null);
        fetchBanners();
        showAlert("Banner updated successfully", "success");
      } else {
        showAlert(data.message || "Update failed", "error");
      }
    } catch (err) {
      console.error("update", err);
      showAlert("Update failed", "error");
    } finally {
      setSaving(false);
    }
  };

  // Pagination calculations
  const totalPages = Math.ceil(banners.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentBanners = banners.slice(startIndex, endIndex);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Custom Alert Component
  const CustomAlert = ({ open, severity, message, onClose }) => {
    if (!open) return null;

    const isSuccess = severity === "success";
    const borderColor = isSuccess ? colors.limeGreen : colors.deleteBg;
    const buttonColor = isSuccess ? colors.darkGreen : colors.red;

    return (
      <Box
        sx={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 9999,
          width: "auto",
          minWidth: { xs: "90vw", sm: 400 },
          maxWidth: "90vw",
        }}
      >
        <Box
          sx={{
            backgroundColor: "white",
            border: `3px solid ${borderColor}`,
            borderRadius: 4,
            padding: 0,
            boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              backgroundColor: borderColor,
              padding: { xs: "12px 16px", sm: "16px 24px" },
              textAlign: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "1.1rem", sm: "1.3rem" },
                fontWeight: 700,
                color: "white",
                textTransform: "uppercase",
              }}
            >
              {isSuccess ? "Success" : "Error"}
            </Typography>
          </Box>
          <Box sx={{ padding: { xs: "16px", sm: "24px" } }}>
            <Typography
              sx={{
                fontSize: { xs: "1rem", sm: "1.1rem" },
                fontWeight: 500,
                textAlign: "center",
                mb: 2,
              }}
            >
              {message}
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                onClick={onClose}
                sx={{
                  backgroundColor: buttonColor,
                  color: "white",
                  fontWeight: 700,
                  borderRadius: 3,
                  px: 4,
                }}
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  // Simple Delete Confirmation Dialog
  const DeleteConfirmationDialog = ({ open, banner, onClose, onConfirm }) => {
    if (!open || !banner) return null;

    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: colors.red,
            color: "white",
            py: 2,
            px: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Delete Banner
          </Typography>
          <IconButton onClick={onClose} sx={{ color: "white" }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete this banner?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Title: <strong>{banner.title || "Untitled Banner"}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            ID: {banner._id.slice(-8)}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: `1px solid ${colors.lightGray}` }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              borderColor: colors.green,
              color: colors.green,
              fontWeight: 600,
              borderRadius: 2,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            variant="contained"
            sx={{
              backgroundColor: colors.red,
              color: "white",
              fontWeight: 600,
              borderRadius: 2,
            }}
            startIcon={<Delete />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: colors.darkWhite,
      }}
    >
      {/* Custom Alert */}
      <CustomAlert
        open={alert.open}
        severity={alert.severity}
        message={alert.message}
        onClose={handleCloseAlert}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteConfirm.open}
        banner={deleteConfirm.banner}
        onClose={closeDeleteConfirm}
        onConfirm={confirmDelete}
      />

      {/* AppBar */}
      <AppBar
        position="sticky"
        sx={{
          bgcolor: "white",
          color: colors.darkBlack,
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
        }}
      >
        <Toolbar
          sx={{
            justifyContent: "space-between",
            gap: 2,
            alignItems: "center",
            minHeight: "64px !important",
            px: { xs: 2, sm: 3 },
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Tabs
              value={0}
              sx={{
                minHeight: "48px",
                "& .MuiTabs-indicator": {
                  backgroundColor: colors.green,
                  height: 3,
                  borderRadius: 3,
                },
                "& .MuiTab-root": {
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
                  minWidth: "auto",
                  px: { xs: 1, sm: 3 },
                  minHeight: "48px",
                  whiteSpace: "nowrap",
                },
                "& .MuiTab-root.Mui-selected": {
                  color: colors.green,
                },
              }}
            >
              <Tab
                icon={<Image sx={{ mr: 1, fontSize: { xs: "18px", sm: "20px" } }} />}
                iconPosition="start"
                label={isMobile ? "Banners" : "All Banners"}
              />
            </Tabs>
          </Box>

          <Chip
            label={`${banners.length} Banners`}
            sx={{
              backgroundColor: colors.lightGreen,
              color: colors.darkGreen,
              fontWeight: 600,
            }}
          />
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          flexGrow: 1,
          overflow: "auto",
          p: { xs: 2, sm: 3 },
        }}
      >
        {/* Upload Section */}
        <Box>
          <Card
            sx={{
              mb: 3,
              p: { xs: 2, sm: 3 },
              borderRadius: 2,
              border: `2px dashed ${colors.green}40`,
              backgroundColor: "#fafafa",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                color: colors.darkGreen,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <UploadFile />
              Upload New Banners
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Button
                component="label"
                variant="contained"
                startIcon={<UploadFile />}
                sx={{
                  backgroundColor: colors.green,
                  "&:hover": { backgroundColor: colors.darkGreen },
                  fontWeight: 600,
                }}
              >
                Select Files
                <input
                  id="banner-files"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFilesChange}
                  hidden
                />
              </Button>
              {uploadFiles.length > 0 && (
                <Chip
                  label={`${uploadFiles.length} file(s) selected`}
                  sx={{ ml: 2, backgroundColor: colors.lightGreen }}
                />
              )}
            </Box>

            {uploadFiles.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 2, color: colors.darkGreen }}
                >
                  Set Titles (Optional)
                </Typography>
                <Stack spacing={2}>
                  {uploadFiles.map((f, idx) => (
                    <TextField
                      key={idx}
                      fullWidth
                      size="small"
                      value={uploadTitles[idx] || ""}
                      onChange={(e) => handleTitleChange(idx, e.target.value)}
                      placeholder={`Title for ${f.name}`}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Title sx={{ color: colors.green }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleUpload}
                disabled={saving || uploadFiles.length === 0}
                startIcon={saving ? <CircularProgress size={18} /> : <UploadFile />}
                sx={{
                  backgroundColor: colors.darkGreen,
                  "&:hover": { backgroundColor: colors.green },
                  fontWeight: 600,
                  px: 3,
                }}
              >
                {saving ? "Uploading..." : "Upload Banners"}
              </Button>
              {uploadFiles.length > 0 && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    setUploadFiles([]);
                    setUploadTitles([]);
                    document.getElementById("banner-files").value = "";
                  }}
                  sx={{
                    borderColor: colors.green,
                    color: colors.green,
                    fontWeight: 600,
                  }}
                >
                  Clear
                </Button>
              )}
            </Box>
          </Card>
        </Box>

        {/* Banner List Section */}
        <Box>
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              color: colors.darkGreen,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Image />
            Current Banners
          </Typography>

          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 6,
              }}
            >
              <CircularProgress size={60} sx={{ color: colors.green }} />
            </Box>
          ) : banners.length === 0 ? (
            <Paper
              sx={{
                textAlign: "center",
                py: 8,
                borderRadius: 2,
                backgroundColor: "#fafafa",
              }}
            >
              <Image sx={{ fontSize: 64, color: colors.midGray, mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No banners found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Upload banners using the form above
              </Typography>
            </Paper>
          ) : (
            <>
              {/* Cards Container using Flexbox */}
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 3,
                  justifyContent: "flex-start",
                  mb: 3,
                }}
              >
                {currentBanners.map((b) => (
                  <Box
                    key={b._id}
                    sx={{
                      // Responsive width for cards
                      width: {
                        xs: "100%", // 1 card per row on mobile
                        sm: "calc((100% - 24px) / 2)", // 2 cards per row on small screens
                        md: "calc((100% - 48px) / 3)", // 3 cards per row on medium screens
                        lg: "calc((100% - 72px) / 4)", // 4 cards per row on large screens
                        xl: "calc((100% - 96px) / 5)", // 5 cards per row on extra large screens
                      },
                      minWidth: {
                        xs: "100%",
                        sm: "200px",
                        lg: "180px",
                      },
                      flexShrink: 0,
                    }}
                  >
                    <Card
                      sx={{
                        width: "100%",
                        height: 310,
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 2,
                        border: `1px solid ${colors.lightGray}`,
                        overflow: "hidden",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
                          borderColor: colors.green,
                        },
                      }}
                    >
                      {/* Fixed height image container */}
                      <Box
                        sx={{
                          position: "relative",
                          width: "100%",
                          height: 145,
                          overflow: "hidden",
                          backgroundColor: "#f5f5f5",
                          flexShrink: 0,
                        }}
                      >
                        <img
                          src={b.imagePath ? `${URL.replace(/\/$/, "")}${b.imagePath}` : ""}
                          alt={b.title || "banner"}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            backgroundColor: "#f5f5f5",
                          }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23f5f5f5'/%3E%3Ctext x='50%' y='50%' font-family='Arial' font-size='14' text-anchor='middle' dy='.3em' fill='%23999'%3EImage%3C/text%3E%3C/svg%3E";
                          }}
                        />
                      </Box>

                      <CardContent 
                        sx={{ 
                          flexGrow: 1, 
                          p: 2, 
                          pt: 1.5,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between"
                        }}
                      >
                        <Box>
                          <Typography
                            variant="subtitle1"
                            fontWeight={600}
                            sx={{ mb: 0.5 }}
                            noWrap
                          >
                            {b.title || "Untitled Banner"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {b._id.slice(-8)}
                          </Typography>
                        </Box>
                      </CardContent>

                      <CardActions sx={{ p: 2, pt: 0, flexShrink: 0, display: "flex", justifyContent: "space-between" }}>
                        <Button
                          size="small"
                          startIcon={<Edit />}
                          onClick={() => openEdit(b)}
                          sx={{
                            color: colors.blue,
                            fontWeight: 600,
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          startIcon={<Delete />}
                          onClick={() => handleDelete(b._id)}
                          sx={{
                            color: colors.red,
                            fontWeight: 600,
                          }}
                        >
                          Delete
                        </Button>
                      </CardActions>
                    </Card>
                  </Box>
                ))}
              </Box>

              {/* Pagination */}
              {totalPages > 1 && (
                <Box 
                  sx={{ 
                    display: "flex", 
                    justifyContent: "center", 
                    mt: 4,
                    pt: 3,
                    borderTop: `1px solid ${colors.lightGray}`
                  }}
                >
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    sx={{
                      '& .MuiPaginationItem-root': {
                        fontWeight: 600,
                      },
                      '& .MuiPaginationItem-page.Mui-selected': {
                        backgroundColor: colors.green,
                        color: 'white',
                        '&:hover': {
                          backgroundColor: colors.darkGreen,
                        },
                      },
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Edit Dialog */}
      {/* Edit Dialog */}
<Dialog
  open={!!editing}
  onClose={() => !saving && setEditing(null)}
  maxWidth="md"
  fullWidth
  PaperProps={{
    sx: {
      borderRadius: 3,
      width: '100%',
      maxWidth: '500px',
    },
  }}
>
  <DialogTitle
    sx={{
      backgroundColor: colors.green,
      color: "white",
      py: 2,
      px: 3,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      mb:2
    }}
  >
    <Typography variant="h6" fontWeight={600}>
      Edit Banner
    </Typography>
    <IconButton
      onClick={() => !saving && setEditing(null)}
      sx={{ color: "white" }}
      disabled={saving}
    >
      <Close />
    </IconButton>
  </DialogTitle>

  <DialogContent sx={{ 
    p: 3,
    width: '100%',
    overflow: 'visible',
  }}>
    <Stack spacing={3} sx={{ width: '100%' }}>
      <TextField
        label="Banner Title"
        fullWidth
        value={editTitle}
        onChange={(e) => setEditTitle(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Title sx={{ color: colors.green }} />
            </InputAdornment>
          ),
        }}
        sx={{
          width: '100%',
          '& .MuiInputBase-root': {
            width: '100%',
          },
          '& .MuiInputBase-input': {
            width: '100%',
            overflow: 'auto',
            textOverflow: 'ellipsis',
          }
        }}
      />

      {/* File Upload */}
      <Box sx={{ width: '100%' }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Replace Image (Optional)
        </Typography>
        <Button
          component="label"
          variant="outlined"
          startIcon={<UploadFile />}
          fullWidth
          sx={{
            borderColor: colors.green,
            color: colors.green,
            py: 1.5,
            width: '100%',
          }}
        >
          {editFile ? editFile.name : "Choose New Image"}
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setEditFile(e.target.files[0])}
            hidden
          />
        </Button>
        {editFile && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ 
              display: "block", 
              mt: 1,
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            Selected: {editFile.name}
          </Typography>
        )}
      </Box>
    </Stack>
  </DialogContent>

  <DialogActions sx={{ p: 3, borderTop: `1px solid ${colors.lightGray}`, width: '100%' }}>
    <Button
      onClick={() => setEditing(null)}
      variant="outlined"
      disabled={saving}
      sx={{
        borderColor: colors.green,
        color: colors.green,
        fontWeight: 600,
        px: 3,
      }}
    >
      Cancel
    </Button>
    <Button
      onClick={handleUpdate}
      variant="contained"
      disabled={saving}
      startIcon={saving ? <CircularProgress size={18} /> : <CheckCircle />}
      sx={{
        backgroundColor: colors.green,
        fontWeight: 600,
        px: 3,
        "&:hover": {
          backgroundColor: colors.darkGreen,
        },
      }}
    >
      {saving ? "Saving..." : "Save Changes"}
    </Button>
  </DialogActions>
</Dialog>
    </Box>
  );
};

export default Banners;